import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';

type ReconciliationFooterProps = {
  totalRows: number;
  resolvedRows: number;
  errorRows: number;
  newContacts: number;
  reusedContacts: number;
  invitesToSend: number;
  onImport: () => void;
  isImporting?: boolean;
  commitError?: string | null;
};

const buildImportLabel = (totalRows: number, resolvedRows: number, errorRows: number) => {
  if (errorRows === 0) return `Import ${totalRows} referrals`;
  if (resolvedRows > 0) return `Import ${resolvedRows} of ${totalRows} referrals`;
  return `Continue with 0 of ${totalRows}`;
};

export const ReconciliationFooter = ({
  totalRows,
  resolvedRows,
  errorRows,
  newContacts,
  reusedContacts,
  invitesToSend,
  onImport,
  isImporting = false,
  commitError,
}: ReconciliationFooterProps) => {
  return (
    <div className="flex items-center justify-between gap-4 border-t border-gray-200 bg-white px-6 py-3">
      {resolvedRows === 0 ? (
        <p className="flex items-center gap-2 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 shrink-0 text-red-600" aria-hidden />
          Nothing will be written — continue to download the annotated CSV.
        </p>
      ) : (
        <p className="flex items-center gap-2 text-sm text-gray-700">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
          creates {newContacts} off-platform contacts · sends {invitesToSend} invitation emails · reuses{' '}
          {reusedContacts} known contacts
          {errorRows > 0 && ` · ${errorRows} rows will return as a CSV after import`}
        </p>
      )}
      {commitError && (
        <p role="alert" className="max-w-sm text-xs text-red-700">
          {commitError}
        </p>
      )}
      <Button onClick={onImport} disabled={isImporting} className="min-w-52 bg-[#026857] hover:bg-[#025045]">
        {isImporting ? 'Importing…' : buildImportLabel(totalRows, resolvedRows, errorRows)}
      </Button>
    </div>
  );
};
